import React, { ComponentProps, memo, ReactNode, useMemo } from 'react'

import styled from '@emotion/styled'

import {
  Title,
  Text,
  withDefaultMaxWidths,
  fadeInScale,
  FlexBox,
  Button,
} from '@surglogs-app/ui'
import { isTransElement } from '@surglogs-app/util'

import { useTranslation } from '../services'

import { DialogBackground } from './DialogBackground'

const StyledContent = styled(FlexBox)`
  ${withDefaultMaxWidths};

  animation: ${fadeInScale} ${({ theme }) => theme.ui.motion.duration.normal}
    ${({ theme }) => theme.ui.motion.easing.decelerate};
  padding: ${({ theme }) => theme.ui.spacings['abs.20']};
  border-radius: ${({ theme }) => theme.ui.spacings['abs.10']};
  max-height: 80vh;
  background-color: ${({ theme }) => theme.ui.colors['white.100']};
  flex-direction: column;
  text-align: left;
  width: auto;
  gap: ${({ theme }) => theme.ui.spacings.md};
`

export interface DefaultDialogProps
  extends ComponentProps<typeof DialogBackground> {
  title?: string
  text?: ReactNode
  onCancel?: () => void
  onConfirm?: () => void
  confirmButtonLabel?: string
  cancelButtonLabel?: string
  buttonVariant?: ComponentProps<typeof Button>['variant']
  withTransparentOverlay?: boolean
  disableConfirmButton?: boolean
}

export const DefaultDialog = memo(
  ({
    onCancel,
    onConfirm,
    title: titleProp,
    text,
    children,
    confirmButtonLabel,
    cancelButtonLabel,
    buttonVariant = 'success',
    withTransparentOverlay = true,
    disableConfirmButton = false,
    ...rest
  }: DefaultDialogProps) => {
    const { t } = useTranslation()
    const title = titleProp ?? t('defaultDialog.title')

    const textContent = useMemo(() => {
      if (!text) {
        return null
      }

      if (typeof text === 'string' || isTransElement(text)) {
        return <Text {...{ paragraph: true, mb: 'xs' }}>{text}</Text>
      }

      return text
    }, [text])

    return (
      <DialogBackground
        {...{
          role: 'dialog',
          'aria-label': 'dialog-content',
          bgc: !withTransparentOverlay ? 'white.100' : undefined,
          ...rest,
        }}>
        <StyledContent
          {...{ shadow: withTransparentOverlay ? 'lg' : undefined }}>
          {title ? <Title {...{ size: 'md' }}>{title}</Title> : null}
          {textContent}
          <FlexBox {...{ gap: 'sm' }}>
            <FlexBox {...{ fd: 'column', gap: 'md' }}>
              {children}
              {onConfirm || onCancel ? (
                <FlexBox {...{ gap: 'sm' }}>
                  {onConfirm ? (
                    <Button
                      {...{
                        variant: buttonVariant,
                        onClick: onConfirm,
                        disabled: disableConfirmButton,
                      }}>
                      {confirmButtonLabel ??
                        t('defaultDialog.confirmButtonLabel')}
                    </Button>
                  ) : null}
                  {onCancel ? (
                    <Button {...{ variant: 'secondary', onClick: onCancel }}>
                      {cancelButtonLabel ?? t('cancelButtonLabel')}
                    </Button>
                  ) : null}
                </FlexBox>
              ) : null}
            </FlexBox>
          </FlexBox>
        </StyledContent>
      </DialogBackground>
    )
  },
)
